import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/services/api'
import { Plan, Package, CreatePackageRequest, UpdatePackageRequest } from '@/types'
import { toast } from 'sonner'

const PLANS_QUERY_KEY = 'plans'
const PACKAGES_QUERY_KEY = 'packages'

export const usePlans = () => {
  return useQuery<Plan[]>({
    queryKey: [PLANS_QUERY_KEY],
    queryFn: async () => {
      const { data } = await api.get('/plans')
      return data
    },
  })
}

export const usePlan = (id: string) => {
  return useQuery<Plan>({
    queryKey: [PLANS_QUERY_KEY, id],
    queryFn: async () => {
      const { data } = await api.get(`/plans/${id}`)
      return data
    },
    enabled: !!id,
  })
}

export const useUpdatePlan = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Plan> }) => {
      const { data } = await api.patch(`/plans/${id}`, updates)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PLANS_QUERY_KEY] })
      toast.success('Plan actualizado')
    },
  })
}

export const useDeletePlan = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/plans/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PLANS_QUERY_KEY] })
      toast.success('Plan eliminado')
    },
    onError: (error: Error) => {
      const axiosErr = error as Error & { response?: { data?: { detail?: string } } }
      const msg = axiosErr.response?.data?.detail || error.message || 'Error al eliminar plan'
      toast.error(msg)
    },
  })
}

export const usePackages = () => {
  return useQuery<Package[]>({
    queryKey: [PACKAGES_QUERY_KEY],
    queryFn: async () => {
      const { data } = await api.get('/packages')
      return data
    },
  })
}

export const usePackage = (id: string) => {
  return useQuery<Package>({
    queryKey: [PACKAGES_QUERY_KEY, id],
    queryFn: async () => {
      const { data } = await api.get(`/packages/${id}`)
      return data
    },
    enabled: !!id,
  })
}

export const useCreatePackage = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (pkg: CreatePackageRequest) => {
      const { data } = await api.post('/packages', pkg)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PACKAGES_QUERY_KEY] })
      toast.success('Paquete creado')
    },
  })
}

export const useUpdatePackage = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: UpdatePackageRequest }) => {
      const { data } = await api.patch(`/packages/${id}`, updates)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PACKAGES_QUERY_KEY] })
      toast.success('Paquete actualizado')
    },
  })
}

export const useTogglePackage = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post(`/packages/${id}/toggle`)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PACKAGES_QUERY_KEY] })
      toast.success('Estado del paquete actualizado')
    },
  })
}

export const useDeletePackage = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/packages/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PACKAGES_QUERY_KEY] })
      toast.success('Paquete eliminado')
    },
    onError: (error: Error) => {
      const axiosErr = error as Error & { response?: { data?: { detail?: string } } }
      const msg = axiosErr.response?.data?.detail || error.message || 'Error al eliminar paquete'
      toast.error(msg)
    },
  })
}
